import { useState, useMemo, useEffect, useRef } from 'react';
import { FiX, FiArrowRight, FiLock, FiMove } from 'react-icons/fi';
import { cpApi } from '../api/api';
import { formatCurrency } from '../utils/formatNumber';
import type { CounterpartyTeam } from '../types';

interface TransferWidgetProps {
  onClose: () => void;
  onSuccess: () => void;
  team: CounterpartyTeam | null;
}

export default function TransferWidget({ onClose, onSuccess, team }: TransferWidgetProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: 24, y: 90 });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isPinned, setIsPinned] = useState(true);
  const dragRef = useRef({ isDragging: false, offsetX: 0, offsetY: 0 });
  const hasMovedRef = useRef(false);

  const [fromId, setFromId] = useState<number | null>(null);
  const [toId, setToId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);

  const participants = useMemo(() => {
    if (!team?.members?.length) return [];
    return team.members.map((m) => {
      const hasSplit = m.salary_balance_available != null || m.salary_balance_held != null;
      const salary_balance_available = hasSplit
        ? (m.salary_balance_available ?? 0)
        : (m.salary_balance ?? 0);
      const salary_balance_held = hasSplit ? (m.salary_balance_held ?? 0) : 0;
      const salary_balance_total = m.salary_balance ?? (salary_balance_available + salary_balance_held);
      return {
        id: m.counterparty_id,
        name: m.is_responsible ? `${m.counterparty_name ?? ''} (ответственный)`.trim() : (m.counterparty_name ?? `Участник #${m.counterparty_id}`),
        salary_balance: salary_balance_total,
        salary_balance_available,
        salary_balance_held,
      };
    });
  }, [team?.members]);

  const filteredFrom = useMemo(() => {
    if (!fromSearch.trim()) return participants;
    const q = fromSearch.toLowerCase();
    return participants.filter((p) => p.name.toLowerCase().includes(q) || String(p.id).includes(q));
  }, [participants, fromSearch]);

  const filteredTo = useMemo(() => {
    const excludeFrom = participants.filter((p) => p.id !== fromId);
    if (!toSearch.trim()) return excludeFrom;
    const q = toSearch.toLowerCase();
    return excludeFrom.filter((p) => p.name.toLowerCase().includes(q) || String(p.id).includes(q));
  }, [participants, fromId, toSearch]);

  const fromParticipant = participants.find((p) => p.id === fromId);
  const toParticipant = participants.find((p) => p.id === toId);

  const amountNum = useMemo(() => {
    const v = amount.replace(',', '.');
    const n = parseFloat(v);
    return isNaN(n) || n <= 0 ? null : n;
  }, [amount]);

  const closeAllDropdowns = () => {
    setShowFromDropdown(false);
    setShowToDropdown(false);
  };

  // Инициализация позиции (справа сверху)
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const width = 480;
    if (hasMovedRef.current || isMobile) return;
    setPos({ x: Math.max(8, window.innerWidth - width - 8), y: 74 });
  }, [isMobile]);

  // Drag & Drop панели
  useEffect(() => {
    if (isMobile) return;
    const onMove = (e: MouseEvent) => {
      if (!dragRef.current.isDragging || isPinned) return;
      const width = panelRef.current?.getBoundingClientRect().width ?? 480;
      const height = panelRef.current?.getBoundingClientRect().height ?? 360;
      const nextX = e.clientX - dragRef.current.offsetX;
      const nextY = e.clientY - dragRef.current.offsetY;
      const clampedX = Math.min(Math.max(8, nextX), window.innerWidth - width - 8);
      const clampedY = Math.min(Math.max(8, nextY), window.innerHeight - Math.min(height, window.innerHeight - 16) - 8);
      setPos({ x: clampedX, y: clampedY });
      hasMovedRef.current = true;
    };
    const onUp = () => { dragRef.current.isDragging = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isPinned, isMobile]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.transfer-widget-relative')) closeAllDropdowns();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fromId || !toId || !amountNum) return;
    if (fromId === toId) return;
    if (comment.length > 50) return;
    setLoading(true);
    try {
      await cpApi.transferTeamInternal({
        from_counterparty_id: fromId,
        to_counterparty_id: toId,
        amount: amountNum,
        comment: comment.trim() || undefined,
      });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { error?: string } | string } })?.response?.data;
      const msg = (typeof res === 'object' && res?.error)
        ? res.error
        : (typeof res === 'string' ? res : (err as Error)?.message)
        || 'Ошибка перевода';
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: `Ошибка: ${msg}`, type: 'error' } }));
    } finally {
      setLoading(false);
    }
  };

  const hasEnoughBalance = fromParticipant && amountNum
    ? fromParticipant.salary_balance_available >= amountNum
    : true;

  return (
    <div
      className={isMobile ? 'fixed inset-0 z-50 flex items-center justify-center p-2' : 'fixed'}
      style={isMobile ? undefined : { left: pos.x, top: pos.y, zIndex: 50 }}
    >
      {isMobile && <div className="absolute inset-0 bg-black/45" onClick={onClose} />}
      <div
        ref={panelRef}
        className={`bg-gradient-to-br from-green-50 to-green-100 dark:from-gray-900 dark:to-gray-800 rounded-xl shadow-2xl border-2 border-green-200 dark:border-green-800/40 ${
          isMobile ? 'relative z-10 w-full max-w-[560px] max-h-[92dvh]' : 'w-[480px] max-w-[calc(100vw-2rem)] max-h-[90vh]'
        } overflow-y-auto`}
      >
        {/* Заголовок */}
        <div
          className={`flex items-center justify-between p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 select-none ${isPinned || isMobile ? 'cursor-default' : 'cursor-move'}`}
          onMouseDown={(e) => {
            if (e.button !== 0 || isPinned || isMobile) return;
            const rect = (panelRef.current?.getBoundingClientRect() ?? { left: pos.x, top: pos.y }) as DOMRect;
            dragRef.current.isDragging = true;
            dragRef.current.offsetX = e.clientX - rect.left;
            dragRef.current.offsetY = e.clientY - rect.top;
          }}
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
              <FiArrowRight className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Транзакция</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isMobile && (
              <button
                type="button"
                onClick={() => setIsPinned((v) => !v)}
                className="group p-2 rounded-lg bg-white/40 hover:bg-white/70 dark:bg-gray-800/50 dark:hover:bg-gray-800 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                title={isPinned ? 'Открепить (разрешить перетаскивание)' : 'Закрепить (запретить перетаскивание)'}
              >
                {isPinned ? (
                  <FiLock className="w-5 h-5 text-green-700 dark:text-green-300 transition-transform duration-200 group-hover:scale-110" />
                ) : (
                  <FiMove className="w-5 h-5 text-green-700 dark:text-green-300 transition-transform duration-200 group-hover:scale-110" />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="group p-2 rounded-lg bg-white/40 hover:bg-white/70 dark:bg-gray-800/50 dark:hover:bg-gray-800 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
              title="Закрыть"
            >
              <FiX className="w-5 h-5 text-gray-600 dark:text-gray-300 transition-transform duration-200 group-hover:rotate-90 group-hover:scale-110" />
            </button>
          </div>
        </div>

        {/* Форма */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* С участника */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              С участника *
            </label>
            <div className="relative transfer-widget-relative">
              <input
                type="text"
                value={fromParticipant ? fromParticipant.name : ''}
                readOnly
                onClick={() => {
                  closeAllDropdowns();
                  setShowFromDropdown(true);
                  setFromSearch('');
                }}
                onFocus={() => {
                  closeAllDropdowns();
                  setShowFromDropdown(true);
                  setFromSearch('');
                }}
                placeholder="Выберите участника..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100"
              />
              {showFromDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-green-300 dark:border-green-800/40 rounded-lg shadow-2xl max-h-60 overflow-hidden flex flex-col ring-2 ring-green-100 dark:ring-green-900/30">
                  <div className="p-2 border-b-2 border-green-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
                    <input
                      type="text"
                      value={fromSearch}
                      onChange={(e) => setFromSearch(e.target.value)}
                      placeholder="Поиск участника..."
                      className="w-full px-3 py-2 border-2 border-green-200 dark:border-gray-600 rounded-lg focus:outline-none focus:border-green-400 bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-48 bg-white dark:bg-gray-800">
                    <button
                      type="button"
                      onClick={() => {
                        setFromId(null);
                        setFromSearch('');
                        setShowFromDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400 italic border-b border-gray-200 dark:border-gray-700"
                    >
                      ✕ Очистить выбор
                    </button>
                    {filteredFrom.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-900/20">Участники не найдены</div>
                    ) : (
                      filteredFrom.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setFromId(p.id);
                            if (toId === p.id) setToId(null);
                            setFromSearch('');
                            setShowFromDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-green-50 dark:hover:bg-green-900/10 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0"
                        >
                          <div className="font-medium text-gray-900 dark:text-gray-100">{p.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            ID: {p.id} • всего {formatCurrency(p.salary_balance)}
                            {(p.salary_balance_held ?? 0) > 0 && (
                              <span className="block text-xs">
                                доступно {formatCurrency(p.salary_balance_available)} • холд {formatCurrency(p.salary_balance_held)}
                              </span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            {fromParticipant && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-300 space-y-0.5">
                <div>
                  Доступно: <span className="font-medium">{formatCurrency(fromParticipant.salary_balance_available)}</span>
                </div>
                {(fromParticipant.salary_balance_held ?? 0) > 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    В холде: {formatCurrency(fromParticipant.salary_balance_held)} (перевод только с доступного)
                  </div>
                )}
              </div>
            )}
          </div>

          {/* На участника */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              На участника *
            </label>
            <div className="relative transfer-widget-relative">
              <input
                type="text"
                value={toParticipant ? toParticipant.name : ''}
                readOnly
                onClick={() => {
                  closeAllDropdowns();
                  setShowToDropdown(true);
                  setToSearch('');
                }}
                onFocus={() => {
                  closeAllDropdowns();
                  setShowToDropdown(true);
                  setToSearch('');
                }}
                placeholder="Выберите участника..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100"
              />
              {showToDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-green-300 dark:border-green-800/40 rounded-lg shadow-2xl max-h-60 overflow-hidden flex flex-col ring-2 ring-green-100 dark:ring-green-900/30">
                  <div className="p-2 border-b-2 border-green-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
                    <input
                      type="text"
                      value={toSearch}
                      onChange={(e) => setToSearch(e.target.value)}
                      placeholder="Поиск участника..."
                      className="w-full px-3 py-2 border-2 border-green-200 dark:border-gray-600 rounded-lg focus:outline-none focus:border-green-400 bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100"
                      autoFocus
                    />
                  </div>
                  <div className="overflow-y-auto max-h-48 bg-white dark:bg-gray-800">
                    <button
                      type="button"
                      onClick={() => {
                        setToId(null);
                        setToSearch('');
                        setShowToDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400 italic border-b border-gray-200 dark:border-gray-700"
                    >
                      ✕ Очистить выбор
                    </button>
                    {filteredTo.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-900/20">Участники не найдены</div>
                    ) : (
                      filteredTo.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => {
                            setToId(p.id);
                            setToSearch('');
                            setShowToDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-green-50 dark:hover:bg-green-900/10 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-0"
                        >
                          <div className="font-medium text-gray-900 dark:text-gray-100">{p.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            ID: {p.id} • всего {formatCurrency(p.salary_balance)}
                            {(p.salary_balance_held ?? 0) > 0 && (
                              <span className="block text-xs">
                                доступно {formatCurrency(p.salary_balance_available)} • холд {formatCurrency(p.salary_balance_held)}
                              </span>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Сумма */}
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Сумма *
            </label>
            <input
              type="text"
              id="amount"
              value={amount}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[\d.,]*$/.test(value)) setAmount(value);
              }}
              placeholder="0.00"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100"
              required
            />
            {fromParticipant && amountNum && (
              <div className={`mt-2 text-sm ${hasEnoughBalance ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-300'}`}>
                {hasEnoughBalance ? (
                  <>Остаток доступного после перевода: {formatCurrency(fromParticipant.salary_balance_available - amountNum)}</>
                ) : (
                  <>Недостаточно доступного баланса (есть ещё в холде: {formatCurrency(fromParticipant.salary_balance_held)})</>
                )}
              </div>
            )}
          </div>

          {/* Комментарий */}
          <div>
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Комментарий ({comment.length}/50)
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 50) setComment(value);
              }}
              rows={3}
              placeholder="Введите комментарий (максимум 50 символов)"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-white dark:bg-gray-900/30 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Кнопки */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              disabled={loading}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading || !fromId || !toId || !amountNum || fromId === toId || !hasEnoughBalance}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg hover:from-green-600 hover:to-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Выполняется...' : 'Выполнить транзакцию'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
